"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { motion } from "framer-motion";

/**
 * Props for `WhatWeOfferSection`.
 */
export type WhatWeOfferSectionProps =
  SliceComponentProps<Content.WhatWeOfferSectionSlice>;

/**
 * Component for "WhatWeOfferSection" Slices.
 */
const WhatWeOfferSection: FC<WhatWeOfferSectionProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className=""
    >
      {slice.variation === "default" ? (
        <div className="bg-[#F8FCFF] py-20">
          <Bounded className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto gap-4 text-center">
            <motion.div
              className="font-archivo font-semibold text-5xl leading-16 text-[#033D61]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="text-secondary">{children}</span>;
                    }
                    return <span className="text-primary">{children}</span>;
                  },
                }}
              />
            </motion.div>
            <motion.div
              className="font-merriweather font-normal text-lg text-left md:text-center md:text-xl leading-8 text-[#033D61]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <PrismicRichText field={slice.primary.description} />
            </motion.div>
          </Bounded>
        </div>
      ) : slice.variation === "empoweringYourCareer" ? (
        <>
          <div className="bg-[#F8FCFF] py-20!">
            <Bounded>
              <div className="space-y-4">
                {/* heading */}

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  viewport={{ once: true }}
                >
                  <PrismicRichText
                    field={slice.primary.heading}
                    components={{
                      heading1: ({ children }) => (
                        <h1 className="text-primary text-5xl leading-[1.33] font-semibold lg:text-center">
                          {children}
                        </h1>
                      ),
                      label: ({ node, children }) => {
                        if (node.data.label === "Highlight") {
                          return (
                            <span className="text-secondary">{children}</span>
                          );
                        }
                      },
                    }}
                  />
                </motion.div>

                {/* sub heading */}

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, ease: "easeInOut", delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <PrismicRichText
                    field={slice.primary.sub_heading}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-xl font-merriweather leading-8 font-normal text-primary md:text-center">
                          {children}
                        </p>
                      ),
                    }}
                  />
                </motion.div>
              </div>
            </Bounded>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default WhatWeOfferSection;
